import { IAIWorkspaceRequest } from "@entities/aiWorkspace";
import { format } from "date-fns";
import { readFollowerCount } from "../CreatorRosterAdd";
import { socialProfileUrl } from "../socialProfileUrl";

export type CreatorFitCheck = NonNullable<
  IAIWorkspaceRequest["creatorFitChecks"]
>[number];

export function getFitScore(check: CreatorFitCheck): number | undefined {
  const score =
    check.briefFitEvaluationJob?.aiAnalysis?.creator_campaign_fit?.overall_score;
  return typeof score === "number" && !isNaN(score) ? score : undefined;
}

function escapeCsvValue(val: string | number | undefined | null): string {
  const text = String(val ?? "");
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function joinReasons(reasons?: string[]) {
  return reasons?.filter(Boolean).join("; ") || "";
}

export function exportCreatorFitChecksToExcel(
  checks: CreatorFitCheck[],
  filename?: string
): void {
  if (checks.length === 0) return;

  const headers = [
    "Creator Name",
    "Username",
    "Platform",
    "Profile URL",
    "Followers",
    "Fit Score",
    "Status",
    "Evaluation Summary",
    "TopMatchReasons",
    "TopMismatchReasons",
    "StrongestEvidence",
    "WeakestEvidence",
  ];

  const rows = checks.map((check) => {
    const sm = check.socialMedia;
    const name = sm?.name || sm?.username || "";
    const username = sm?.username ? `@${sm.username}` : "";
    const platform = sm?.platform || "";
    const profileUrl =
      socialProfileUrl(platform, sm?.username) ||
      (sm?.username
        ? `https://www.${platform || "instagram"}.com/${sm.username}`
        : "");
    const followers = readFollowerCount(sm) ?? "";
    const fitScore = getFitScore(check) ?? "";
    const status = check.briefFitEvaluationJob?.status || "";
    const analysis = check.briefFitEvaluationJob?.aiAnalysis;
    const fit = analysis?.creator_campaign_fit;
    const fitReasons = analysis?.fit_reasons;
    const summary =
      fit?.frontend_summary ||
      fit?.one_line_summary ||
      (fit?.verdict ? `Verdict: ${fit.verdict}` : "");

    return [
      name,
      username,
      platform,
      profileUrl,
      followers,
      fitScore,
      status,
      summary,
      joinReasons(fitReasons?.top_match_reasons),
      joinReasons(fitReasons?.top_mismatch_reasons),
      joinReasons(fitReasons?.strongest_evidence),
      joinReasons(fitReasons?.weakest_evidence),
    ]
      .map(escapeCsvValue)
      .join(",");
  });

  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([`\uFEFF${csv}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filename || `creator-fit-creators-${format(new Date(), "yyyy-MM-dd")}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
